import { useEffect, useRef, useState } from 'react'
import {
  chatbotService, chatErrorKey, isValidChatMessage,
  type ChatHistoryEntry, type ChatResponse, type SessionPage,
} from '@/services/chatbotService'

export interface ChatMessage extends ChatHistoryEntry {
  id: string
  timestamp: Date
  response?: ChatResponse
}

export function useChatConversation() {
  // Only completed exchanges enter the transcript and future request history.
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [pendingMessage, setPendingMessage] = useState<ChatMessage | null>(null)
  const [inputValue, setInputValueState] = useState('')
  const [error, setError] = useState<string | null>(null)
  const pendingRequest = useRef<AbortController | null>(null)
  const failedRequest = useRef<{ text: string; sessionId?: number; sessionPage?: SessionPage } | null>(null)

  const setInputValue = (text: string) => {
    failedRequest.current = null
    setInputValueState(text)
  }

  useEffect(() => () => {
    pendingRequest.current?.abort()
    pendingRequest.current = null
  }, [])

  const reset = () => {
    pendingRequest.current?.abort()
    pendingRequest.current = null
    setMessages([])
    setPendingMessage(null)
    setInputValue('')
    setError(null)
  }

  const send = async (text: string, sessionId?: number, sessionPage?: SessionPage) => {
    // The ref guards rapid submissions before React commits the loading state.
    if (pendingRequest.current) return
    if (!isValidChatMessage(text)) {
      setError('chatBotErrorInvalid')
      return
    }
    const controller = new AbortController()
    // A retry of a selection must retain its explicit identity. Editing the
    // composer or resetting the conversation clears this saved request.
    if (sessionId === undefined && sessionPage === undefined && failedRequest.current?.text === text) {
      sessionId = failedRequest.current.sessionId
      sessionPage = failedRequest.current.sessionPage
    }
    failedRequest.current = null
    pendingRequest.current = controller
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(), role: 'user', content: text.trim(), timestamp: new Date(),
    }
    setPendingMessage(userMessage)
    setInputValue('')
    setError(null)

    try {
      const history = messages.map(({ role, content }) => ({ role, content }))
      const response = await chatbotService.ask(text, history, controller.signal, sessionId, sessionPage)
      if (pendingRequest.current !== controller) return
      setMessages(previous => [...previous, userMessage, {
        id: crypto.randomUUID(), role: 'assistant', content: response.answer,
        timestamp: new Date(), response,
      }])
    } catch (requestError) {
      if (pendingRequest.current !== controller) return
      failedRequest.current = { text, sessionId, sessionPage }
      setInputValueState(text)
      setError(chatErrorKey(requestError))
    } finally {
      if (pendingRequest.current === controller) {
        pendingRequest.current = null
        setPendingMessage(null)
      }
    }
  }

  return {
    messages, pendingMessage, inputValue, setInputValue, error, send, reset,
    isLoading: pendingMessage !== null,
    canSend: pendingMessage === null && isValidChatMessage(inputValue),
  }
}
